import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { trans } from '@spotahome/soyuz/client';

import classNames from 'classnames';

import { LandlordScoringShape } from '../../constants/shapes';

import { CHANNELS, Button, Tag, useMediatorModalHandle } from '../../../..';
import { DSIcon } from '../../../base';

import { getDateAsUTC, yearsSinceDate } from '../utils/dateUtils';

import PrivateLandlord from '../icons/PrivateLandlord';
import ProfessionalLandlord from '../icons/ProfessionalLandlord';

import Scoring from '../Scoring';

import {
  LANDLORD_TYPES,
  PRIVATE_LANDLORD,
  PROFESSIONAL_LANDLORD
} from './constants';

import './ExtendedLandlordInformationContent.scss';

const LandlordInformationPiece = ({ title, subtitle, isRedesign = false }) => (
  <div
    className={classNames('extended-landlord-information-piece', {
      'extended-landlord-information-piece--redesign': isRedesign
    })}
  >
    <span className="extended-landlord-information-piece__title">{title}</span>
    <span className="extended-landlord-information-piece__subtitle">
      {subtitle}
    </span>
  </div>
);

LandlordInformationPiece.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  subtitle: PropTypes.string.isRequired,
  isRedesign: PropTypes.bool
};

const MAIN_LANGUAGES = [
  'spanish',
  'english',
  'portuguese',
  'french',
  'german',
  'italian'
];

const noop = () => {};
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const ExtendedLandlordInformationContent = ({
  type,
  joiningDate,
  listingsPublished,
  averageResponseTime,
  numberOfBookingRequests = null,
  languages = EMPTY_ARRAY,
  renderOtherProperties = noop,
  firstName = null,
  landlordScoring = EMPTY_OBJECT,
  isRedesign = false
}) => {
  const { handleModalOpen } = useMediatorModalHandle({
    channel: CHANNELS.OPEN_VERIFICATION_MODAL
  });

  const filteredCommonLanguages = languages.filter(language =>
    MAIN_LANGUAGES.includes(language)
  );

  const spokenLanguagesTranslated = filteredCommonLanguages
    .map(language => trans(`landlord_language_${language}`))
    .join(', ');

  const LANDLORD_ICONS = {
    [PRIVATE_LANDLORD]: () => <PrivateLandlord />,
    [PROFESSIONAL_LANDLORD]: () => <ProfessionalLandlord />
  };

  const timeWithSpotahome = joiningDate ? yearsSinceDate(joiningDate) : null;
  const hasScoring =
    landlordScoring?.score && landlordScoring?.totalBookingsClosed;

  return (
    <Fragment>
      <div
        className={classNames('extended-landlord-information-content__header', {
          'extended-landlord-information-content__header--redesign': isRedesign
        })}
      >
        <div className="extended-landlord-information-content__info">
          <div className="extended-landlord-information-content__icon">
            {LANDLORD_ICONS[type]()}
          </div>
          <div className="extended-landlord-information-content__title">
            <div className="extended-landlord-information-content__type">
              {firstName
                ? `${firstName} - ${trans(
                    `listing.landlord_info.type.${type}`
                  )}`
                : trans(`listing.landlord_info.type.${type}`)}
            </div>
            {timeWithSpotahome && isRedesign && (
              <div className="extended-landlord-information-content__landlord-info">
                {trans('listing.landlord_info.time_with_spotahome', {
                  time: timeWithSpotahome
                })}
              </div>
            )}
            <div className="extended-landlord-information-content__landlord-info">
              {trans('listing.landlord_info.languages', {
                langs:
                  spokenLanguagesTranslated ||
                  trans('landlord_language_english')
              })}
            </div>
            {hasScoring && !isRedesign && (
              <Scoring
                score={landlordScoring.score}
                totalReviews={landlordScoring.totalBookingsClosed}
                size="huge"
                bold={false}
                tooltipPosition="right"
              />
            )}
          </div>
          {hasScoring && isRedesign && (
            <Scoring
              buttonClassName="extended-landlord-information-content__scoring"
              score={landlordScoring.score}
              totalReviews={landlordScoring.totalBookingsClosed}
              size="huger"
              bold={false}
              tooltipPosition="top"
            />
          )}
          {!isRedesign && (
            <div className="extended-landlord-information-content__verified extended-landlord-information-content__verified--hidden-in-desktop">
              <Tag
                onClick={handleModalOpen}
                label={trans('verified.by.spotahome').toUpperCase()}
                isSpaced
                renderLeadingIcon={() => (
                  <DSIcon
                    name="check_circle_transparent"
                    className="extended-landlord-information-content__verified-icon"
                  />
                )}
              />
            </div>
          )}
        </div>
        {isRedesign && (
          <Button
            className="extended-landlord-information-content__verified"
            onClick={handleModalOpen}
            color="link"
          >
            <DSIcon name="verified_user" size="m" />
            <span className="extended-landlord-information-content__scoring-text">
              {trans('verified.by.spotahome.identity')}
            </span>
          </Button>
        )}
        {!isRedesign && (
          <div className="extended-landlord-information-content__verified extended-landlord-information-content__verified--hidden-in-mobile">
            <Tag
              onClick={handleModalOpen}
              label={trans('verified.by.spotahome').toUpperCase()}
              isSpaced
              labelClassName="extended-landlord-information-content__verified-label"
              renderLeadingIcon={() => (
                <DSIcon
                  name="check_circle_transparent"
                  className="extended-landlord-information-content__verified-icon"
                />
              )}
            />
          </div>
        )}
      </div>

      <div
        className={classNames('extended-landlord-information-content__list', {
          'extended-landlord-information-content__list--redesign': isRedesign
        })}
      >
        <LandlordInformationPiece
          title={listingsPublished}
          subtitle={trans('listing.landlord_info.published.subtitle')}
          isRedesign={isRedesign}
        />

        {joiningDate && !isRedesign && (
          <LandlordInformationPiece
            title={trans('listing.landlord_info.with_us.title', {
              year: getDateAsUTC(new Date(joiningDate)).getFullYear()
            })}
            subtitle={trans('listing.landlord_info.with_us.subtitle')}
          />
        )}
        {numberOfBookingRequests > 0 ? (
          <>
            <LandlordInformationPiece
              title={numberOfBookingRequests}
              subtitle={trans(
                'listing.landlord_info.booking_requests.subtitle'
              )}
              isRedesign={isRedesign}
            />
            <LandlordInformationPiece
              title={averageResponseTime}
              subtitle={trans(
                'listing.landlord_info.average_response_time.subtitle'
              )}
              isRedesign={isRedesign}
            />
          </>
        ) : null}
      </div>
      {renderOtherProperties()}
    </Fragment>
  );
};

ExtendedLandlordInformationContent.propTypes = {
  type: PropTypes.oneOf(LANDLORD_TYPES).isRequired,
  joiningDate: PropTypes.string.isRequired,
  listingsPublished: PropTypes.number.isRequired,
  averageResponseTime: PropTypes.string.isRequired,
  numberOfBookingRequests: PropTypes.number,
  languages: PropTypes.arrayOf(PropTypes.string),
  renderOtherProperties: PropTypes.func,
  firstName: PropTypes.string,
  landlordScoring: PropTypes.shape(LandlordScoringShape),
  isRedesign: PropTypes.bool
};

export default ExtendedLandlordInformationContent;
